import { formatDate } from '@/shared/lib/dateUtils'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Trash2, Calendar, ArrowLeft
} from 'lucide-react';
import { useNotifications } from '@/context/NotificationsContext';
import {
    type ApiNotification,
    filterNotificationsByModule,
} from '@/services/notificationsService';
import trashAlertIcon from '@/assets/trash-alert.svg';
import { Checkbox } from '@/shared/components/Checkbox';
import { useFilaSaliente } from '@/shared/hooks/useFilaSaliente';

interface NotificationsPageProps {
    ctx?: {
        notifications: ApiNotification[];
        loading: boolean;
        markAsRead: (id: string) => Promise<void>;
        deleteNotifications: (ids: string[]) => Promise<void>;
    };
}

export const NotificationsPage = ({ ctx }: NotificationsPageProps = {}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const adminCtx = useNotifications();
    const { notifications, loading, markAsRead, deleteNotifications } = ctx ?? adminCtx;

    const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [_isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { claseDeFila, animarSalida } = useFilaSaliente();
    const [_forceEmptyState, setForceEmptyState] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (loading && notifications.length === 0) {
            timer = setTimeout(() => {
                setForceEmptyState(true);
            }, 2000);
        } else if (notifications.length > 0) {
            setForceEmptyState(false);
        }
        return () => clearTimeout(timer);
    }, [loading, notifications]);

    const handleBack = () => {
        const isSupplierPath = location.pathname.startsWith('/proveedores');
        navigate(isSupplierPath ? '/proveedores/dashboard' : '/dashboard');
    };

    const toggleSelection = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    // El mismo filtro por módulo que la campanita. Sin esto, el listado
    // completo mostraba notificaciones de todos los módulos y no cuadraba con
    // lo que se acababa de ver en el desplegable.
    //
    // En el portal del proveedor no aplica: ahí las notificaciones llegan por
    // su propio endpoint y `ctx` viene definido.
    const delModulo = ctx
        ? notifications
        : filterNotificationsByModule(notifications, location.pathname);

    const filteredNotifications = delModulo.filter(n => {
        if (activeTab === 'unread') return !n.isRead;
        if (activeTab === 'read') return n.isRead;
        return true;
    });

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredNotifications.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredNotifications.map(n => n.id));
        }
    };

    const confirmDelete = async () => {
        setShowDeleteModal(false);
        setIsDeleting(true);
        // A-16: las seleccionadas salen y hasta entonces se borran.
        await animarSalida(selectedIds);
        await deleteNotifications(selectedIds);
        setSelectedIds([]);
        setIsDeleting(false);
    };

    const isAllSelected =
        filteredNotifications.length > 0 &&
        selectedIds.length === filteredNotifications.length;

    /**
     * Estado intermedio del «seleccionar todas». Con algunas marcadas la
     * casilla se veía destildada, igual que sin nada seleccionado.
     *
     * Va por `ref` y no como prop porque `indeterminate` es una propiedad del
     * nodo, no un atributo: React no la escribe al pintar.
     */
    const casillaDeTodas = useRef<HTMLInputElement | null>(null);
    const haySeleccionParcial = selectedIds.length > 0 && !isAllSelected;

    useEffect(() => {
        if (casillaDeTodas.current) {
            casillaDeTodas.current.indeterminate = haySeleccionParcial;
        }
    }, [haySeleccionParcial]);

    return (
        <div className="min-h-screen bg-gray-50/50 p-6 relative">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* HEADER CON BOTÓN VOLVER INTEGRADO */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBack}
                            className={estiloDeBoton('secundario')}
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Volver
                        </button>

                        <div className="h-8 w-px bg-gray-200 mx-1"></div> {/* Separador visual */}

                        <div>
                            <TituloDePantalla>Notificaciones</TituloDePantalla>
                            <p className="text-gray-500 text-sm">Administra aquí tus notificaciones</p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        {selectedIds.length > 0 && (
                            <>
                                <button
                                    onClick={() => {
                                        selectedIds.forEach(id => {
                                            const n = notifications.find(x => x.id === id);
                                            if (n && !n.isRead) markAsRead(id);
                                        });
                                        setSelectedIds([]);
                                    }}
                                    className={estiloDeBoton('primario')}
                                >
                                    Marcar seleccionadas
                                </button>

                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className={estiloDeBoton('peligro')}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Eliminar
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* TABS */}
                <div className="flex gap-2">
                    {['all', 'unread', 'read'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab as any);
                                setSelectedIds([]);
                            }}
                            className={`w-36 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === tab ? 'bg-[#315FB9] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {tab === 'all' && 'Todas'}
                            {tab === 'unread' && 'Sin leer'}
                            {tab === 'read' && 'Leídas'}
                        </button>
                    ))}
                </div>

                {/* TABLA */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="flex bg-[#F3F2F7] border-b border-gray-200">
                        <div className="w-16 flex justify-center items-center py-4">
                            <Checkbox
                                ref={casillaDeTodas}
                                checked={isAllSelected}
                                onChange={toggleSelectAll}
                                aria-label="Seleccionar todas las notificaciones"
                            />
                        </div>
                        <div className="flex-1 py-4 font-semibold text-gray-700">Notificación</div>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {filteredNotifications.map(notif => {
                            const isSelected = selectedIds.includes(notif.id);
                            let bg = "bg-white";
                            if (isSelected) bg = "bg-[#E8F0FF]";
                            else if (!notif.isRead) bg = "bg-[#F4F7FB]";

                            return (
                                <div
                                    key={notif.id}
                                    onClick={async () => {
                                        if (!notif.isRead) {
                                            await markAsRead(notif.id);
                                        }
                                    }}
                                    className={`flex cursor-pointer ${claseDeFila(notif.id)} ${bg} ${!isSelected ? 'hover:bg-[#F0F5FF]' : ''} transition-colors`}
                                >
                                    {/* La celda entera es la etiqueta: se puede
                                        picar junto a la casilla y no solo
                                        encima. El stopPropagation evita que
                                        seleccionar marque además como leída. */}
                                    <label
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-16 flex justify-center items-center cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => toggleSelection(notif.id)}
                                            aria-label={`Seleccionar ${notif.title}`}
                                        />
                                    </label>

                                    <div className="flex-1 py-4 pr-6">
                                        <div className="flex justify-between mb-1">
                                            <p className={`font-semibold ${!notif.isRead ? 'text-gray-900' : 'text-gray-500'}`}>
                                                {notif.title}
                                            </p>
                                            <span className="text-xs text-gray-400 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(notif.createdAt)}
                                            </span>
                                        </div>
                                        <p className={`text-sm ${!notif.isRead ? 'text-gray-700' : 'text-gray-400'}`}>
                                            {notif.message}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MODAL ELIMINAR */}
            {showDeleteModal && (
                <CapaDeModal zIndex={100}>
                    <div className="bg-white rounded-2xl p-6 w-[400px] text-center">
                        <img src={trashAlertIcon} alt="Trash" className="w-16 h-16 mx-auto mb-4" />
                        <h3 className="text-lg font-bold mb-2">¿Eliminar notificaciones?</h3>
                        <p className="text-gray-500 mb-6">Esta acción no se puede deshacer.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowDeleteModal(false)} className={estiloDeBoton('secundario', 'flex-1')}>Cancelar</button>
                            <button onClick={confirmDelete} className={estiloDeBoton('peligro', 'flex-1')}>Eliminar</button>
                        </div>
                    </div>
                </CapaDeModal>
            )}
        </div>
    );
};